import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import { css } from 'styled-components';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import { Banner, CuiOptionType, TextField } from '@crehana/ui';
import {
  customLocalStorage,
  customSessionStorage,
  parseQueryStrings,
} from '@crehana/utils';

import {
  OrganizationSizeEnum,
  OriginLeadSectionEnum,
  OriginRequestEnum,
} from '@/shared/types/graphql/globals';
import { REGEX_EMAIL } from '@/views/B2BLanding/constants';
import { AMPLITUDE_TRACKS } from '@/views/B2BLanding/events';
import REQUEST_ORGANIZATION_DEMO from '@/views/B2BLanding/pages/FreeTrial/graphql/CreateOrganizationRequestDemo.graphql';

import PrimaryButton from '../../../../components/PrimaryButton';
import {
  CreateOrganizationRequestDemo,
  CreateOrganizationRequestDemoVariables,
} from '../../../../graphql/types/CreateOrganizationRequestDemo';
import {
  COUNTRIES,
  FormSteps,
  getDepartmentOptions,
  getRoleOptions,
  INPUT_MAX_LENGTH,
} from '../../constants';
import useFormStepsContext from '../../hooks/useFormStepsContext';
import Dropdown from '../UserDataFields/Dropdown';
import NameField, { NameState } from '../UserDataFields/NameField';
import LazyPhoneInput from '../UserDataFields/PhoneInput';
import ArrowBack from './ArrowBack';
import CountCompany from './CompanySize';

interface FormStepsProps {
  setLastStep: () => void;
}

const FormBySteps: FC<React.PropsWithChildren<FormStepsProps>> = ({
  setLastStep,
}) => {
  const { t, countryCode } = useTranslation();
  const { logEvent } = useAmplitude();
  const roleOptions = getRoleOptions(t);
  const departmentOptions = getDepartmentOptions(t);
  const ref = useRef<HTMLButtonElement>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [error, setError] = useState<string | undefined>(undefined);
  const [name, setName] = useState<NameState>({ isValid: false, name: '' });
  const [lastName, setLastName] = useState<NameState>({
    isValid: false,
    name: '',
  });
  const [phone, setPhone] = useState({
    fullNumber: '',
    code: '',
    phoneNumber: '',
    error: '',
    isValid: false,
  });
  const [country, setCountry] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [companySize, setCompanySize] = useState<
    CuiOptionType<OrganizationSizeEnum> | undefined
  >(undefined);
  const setStep = (step: number) => {
    if (step > 7) {
      return;
    }
    setCurrentStep(step);
  };
  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
    errors,
    watch,
    control,
  } = useForm<CreateOrganizationRequestDemoVariables>({
    mode: 'onChange',
  });

  const {
    email: emailValidation,
    company: companyValidation,
    role,
    department,
  } = watch();

  const {
    setUserInteraction,
    setStep: setStepCxt,
    setNameUser,
  } = useFormStepsContext();

  /**
   *  company_segment comes from /planes/ -> Lite, Core and Elevate
   */
  const companySegmentKey = 'company_segment';
  const companySizeValue = customLocalStorage.getItem(
    companySegmentKey,
  ) as OrganizationSizeEnum;
  const hasCompanySize = Boolean(companySizeValue);
  const organizationSize = hasCompanySize
    ? companySizeValue
    : companySize?.value;

  const [requestOrganizationDemo] = useMutation<
    CreateOrganizationRequestDemo,
    CreateOrganizationRequestDemoVariables
  >(REQUEST_ORGANIZATION_DEMO, {
    onCompleted({ createOrganizationRequestDemo }) {
      if (createOrganizationRequestDemo?.success) {
        logEvent({
          event: AMPLITUDE_TRACKS.LEAD_SENT_VARIANT,
          data: {
            ...getSourceEventParams(),
            Product: 'B2B Landing',
            Step: FormSteps.FORM_STEP_PHONE,
          },
        });

        logEvent({
          event: 'B2B MQL',
          data: {
            ...getSourceEventParams(),
            Product: 'Carolina Business',
          },
        });

        setLastStep();
        customLocalStorage.removeItem(companySegmentKey);
        // This is important to clear the sessionStorage after the data has been sent
        customSessionStorage.removeItem('utm_source');
        customSessionStorage.removeItem('utm_medium');
        customSessionStorage.removeItem('utm_campaign');
        customSessionStorage.removeItem('utm_term');
        customSessionStorage.removeItem('utm_content');
      }
    },
    onError({ message }) {
      setError(message);
    },
  });

  const onSubmit = (data: CreateOrganizationRequestDemoVariables) => {
    const { email, company, role, department } = data;
    const detailResourceUrl = customSessionStorage.getItem('detailResourceUrl');
    const typeResource = customSessionStorage.getItem('resourceType');
    const {
      utm_source: queryUtmSource,
      utm_medium: queryUtmMedium,
      utm_campaign: queryUtmCampaign,
      utm_term: queryUtmTerm,
      utm_content: queryUtmContent,
      gclid,
      fbclid,
    } = parseQueryStrings(window.location.search) || {};

    const sessionUtmSource = customSessionStorage.getItem('utm_source');
    const sessionUtmMedium = customSessionStorage.getItem('utm_medium');
    const sessionUtmCampaign = customSessionStorage.getItem('utm_campaign');
    const sessionUtmTerm = customSessionStorage.getItem('utm_term');
    const sessionUtmContent = customSessionStorage.getItem('utm_content');

    const utmSource = sessionUtmSource || queryUtmSource?.toString();
    const utmMedium = sessionUtmMedium || queryUtmMedium?.toString();
    const utmCampaign = sessionUtmCampaign || queryUtmCampaign?.toString();
    const utmTerm = sessionUtmTerm || queryUtmTerm?.toString();
    const utmContent = sessionUtmContent || queryUtmContent?.toString();

    if (organizationSize && country) {
      const payload: Record<
        'variables',
        CreateOrganizationRequestDemoVariables
      > = {
        variables: {
          name: name.name,
          lastName: lastName.name,
          phoneNumber: phone.fullNumber,
          email,
          organizationSize,
          country: country.value,
          company,
          role,
          department,
          recaptchaToken: '',
          utmSource,
          utmMedium,
          utmCampaign,
          utmTerm,
          utmContent,
          gclid: gclid ? gclid.toString() : undefined,
          fbclid: fbclid ? fbclid.toString() : undefined,
          origin: detailResourceUrl
            ? OriginRequestEnum.DOWNLOADABLE_RESOURCE
            : OriginRequestEnum.REGISTER,
          slug: detailResourceUrl,
          typeResource,
          // TODO: Feature is pending to get values for these two variables
          newsAndPromotionWereChecked: false,
          requestDemoWasChecked: false,
          originLeadSection: OriginLeadSectionEnum.B2B_FREE_TRIAL_LANDING,
        },
      };

      return requestOrganizationDemo(payload);
    }
  };

  const nextStep = (amplitudeTrack: string) => {
    if (amplitudeTrack) {
      logEvent({
        event: AMPLITUDE_TRACKS.LEAD_SENT_VARIANT,
        data: {
          ...getSourceEventParams(),
          Product: 'B2B Landing',
          Step: amplitudeTrack,
        },
      });
    }

    const isNextStepIsCompanySize = currentStep === 3;
    const skipCompanySize = hasCompanySize && isNextStepIsCompanySize;
    const interval = skipCompanySize ? 2 : 1;

    setStep(currentStep + interval);
    setStepCxt(currentStep + interval);
    setUserInteraction(null);
  };

  const backStep = () => {
    const isPreviousStepCompanySize = currentStep === 5;
    const skipCompanySize = hasCompanySize && isPreviousStepCompanySize;
    const interval = skipCompanySize ? 2 : 1;

    setStep(currentStep - interval);
    setStepCxt(currentStep - interval);
    setUserInteraction(null);
  };

  const validations = {
    required: { value: true, message: t('VALIDATIONS_REQUIRED') },
    maxLength: {
      value: INPUT_MAX_LENGTH,
      message: t('VALIDATIONS_MAX_LENGTH', {
        interpolation: { max: INPUT_MAX_LENGTH },
      }),
    },
  };

  const enterHandler = (
    e: KeyboardEvent<HTMLInputElement>,
    amplitudeTrack: string,
  ) => {
    if (e.code === 'Enter') {
      nextStep(amplitudeTrack);
    }
  };

  useEffect(() => {
    setNameUser(name.name);
    setUserInteraction(`${name.name} ${lastName.name}`);
  }, [name, lastName, setNameUser, setUserInteraction]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      id="b2b-landing-form"
      className="m-auto mt-24 sm:mt-32"
    >
      {currentStep >= 1 && <ArrowBack backStep={backStep} />}
      {/* Full Name */}
      <div
        className={`${
          currentStep === 0 ? 'block' : 'hidden'
        } m-auto flex justify-between flex-wrap`}
        css={css`
          ${({ theme }) => theme.breakpoints.up('md')} {
            max-width: 578px;
          }
        `}
      >
        <div className="w-full sm:w-2/4 sm:pr-8">
          <NameField
            onChange={setName}
            placeholder={t('FORM_BY_STEPS_NAME_HINT')}
          />
        </div>
        <div className="mt-20 sm:mt-0 w-full sm:w-2/4 sm:pl-8">
          <NameField
            onChange={setLastName}
            isLastName
            placeholder={t('FORM_BY_STEPS_LASTNAME_HINT')}
          />
        </div>
        <div className="mt-40 w-full text-center">
          <PrimaryButton
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            disabled={!name.isValid || !lastName.isValid}
            onClick={() => nextStep(FormSteps.FORM_STEP_FULLNAME)}
            isDark
          />
        </div>
      </div>
      {/* Country */}
      <div
        className={`${currentStep === 1 ? 'block' : 'hidden'} m-auto`}
        css={css`
          max-width: 363px;
        `}
      >
        <Dropdown
          label={t('FORM_BY_STEPS_COUNTRY_LABEL')}
          hint={t('FORM_BY_STEPS_COUNTRY_HINT')}
          name="country"
          full
          inputRef={register}
          options={COUNTRIES.map(company => {
            return { value: company.value, label: t(company.label) };
          })}
          selected={country}
          onSelected={e => {
            setCountry(e);
            setUserInteraction(e?.label);
          }}
          data-testid="country"
          className="mb-8"
          isDark
        />
        {errors.country?.message && (
          <span className="font-poppins font-normal text-12 text-red-main">
            {errors.country.message}
          </span>
        )}

        <div className="mt-40 w-full text-center">
          <PrimaryButton
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            disabled={!country}
            onClick={() => nextStep(FormSteps.FORM_STEP_COUNTRY)}
            isDark
          />
        </div>
      </div>
      {/* Email */}
      <div
        className={`${currentStep === 2 ? 'block' : 'hidden'} m-auto`}
        css={css`
          max-width: 363px;
        `}
      >
        <div>
          <span className="font-poppins font-semibold text-16 text-white">
            {t('FORM_BY_STEPS_EMAIL_LABEL')}
          </span>
          <TextField
            hint={t('FORM_BY_STEPS_EMAIL_HINT')}
            name="email"
            data-testid="email"
            maxLength={50}
            ref={register({
              ...validations,
              pattern: {
                value: REGEX_EMAIL,
                message: t('FORM_BY_STEPS_EMAIL_ERROR'),
              },
            })}
            full
            onChange={({ target }) => setUserInteraction(target.value)}
            onKeyDown={e => {
              if (!errors.email?.message && emailValidation) {
                enterHandler(e, FormSteps.FORM_STEP_EMAIL);
              }
            }}
            inputClassName="font-poppins font-normal text-14"
            wrapperClassName="rounded-10"
            className="my-8"
            darkMode
          />
          {errors.email?.message && (
            <span className="font-poppins font-normal text-12 text-red-main">
              {errors.email.message}
            </span>
          )}
        </div>
        <div className="mt-40 w-full text-center">
          <PrimaryButton
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            disabled={Boolean(errors.email?.message) || !emailValidation}
            onClick={() => nextStep(FormSteps.FORM_STEP_EMAIL)}
            isDark
          />
        </div>
      </div>
      {/* Company */}
      <div
        className={`${currentStep === 3 ? 'block' : 'hidden'} m-auto`}
        css={css`
          max-width: 363px;
        `}
      >
        <div>
          <span className="font-poppins font-semibold text-16 text-white">
            {t('FORM_BY_STEPS_COMPANY_LABEL')}
          </span>
          <TextField
            hint={t('FORM_BY_STEPS_COMPANY_HINT')}
            name="company"
            data-testid="company"
            ref={register(validations)}
            maxLength={50}
            full
            onChange={({ target }) => setUserInteraction(target.value)}
            onKeyDown={e => {
              if (!errors.company?.message && companyValidation) {
                enterHandler(e, FormSteps.FORM_STEP_COMPANY);
              }
            }}
            inputClassName="font-poppins font-normal text-14"
            wrapperClassName="rounded-10"
            className="my-8"
            darkMode
          />
          {errors.company?.message && (
            <span className="font-poppins font-normal text-12 text-red-main">
              {errors.company.message}
            </span>
          )}
        </div>
        <div className="mt-40 w-full text-center">
          <PrimaryButton
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            disabled={Boolean(errors.company?.message) || !companyValidation}
            onClick={() => nextStep(FormSteps.FORM_STEP_COMPANY)}
            isDark
          />
        </div>
      </div>
      {/* Company size */}
      <CountCompany
        className={`${currentStep === 4 ? 'block' : 'hidden'} m-auto`}
        nextStep={() => nextStep(FormSteps.FORM_STEP_COMPANY_SIZE)}
        setCompanySize={({ value, label }) => {
          setUserInteraction(label);
          setCompanySize({ value, label });
        }}
      />
      {/* Role */}
      <div
        className={`${currentStep === 5 ? 'block' : 'hidden'} m-auto`}
        css={css`
          max-width: 363px;
        `}
      >
        <div>
          <Controller
            control={control}
            name="role"
            rules={{
              required: true,
            }}
            render={({ onChange, value }) => (
              <Dropdown
                className="rounded-10 mb-40"
                hint={t('USER_ROLE_HINT')}
                label={t('USER_ROLE_LABEL')}
                data-testid="role-dropdown"
                options={roleOptions}
                inputRef={register}
                selected={roleOptions.find(_role => _role.value === value)}
                onSelected={option => {
                  if (option) {
                    onChange(option.value);
                  }
                }}
                isDark
                full
              />
            )}
          />
        </div>
        <div className="mt-40 w-full text-center">
          <PrimaryButton
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            disabled={!role}
            onClick={() => nextStep(FormSteps.FORM_STEP_ROLE)}
            isDark
          />
        </div>
      </div>
      {/* Department */}
      <div
        className={`${currentStep === 6 ? 'block' : 'hidden'} m-auto`}
        css={css`
          max-width: 363px;
        `}
      >
        <div>
          <Controller
            control={control}
            name="department"
            rules={{
              required: true,
            }}
            render={({ onChange, value }) => (
              <Dropdown
                className="rounded-10 mb-40"
                hint={t('USER_DEPARTMENT_HINT')}
                label={t('USER_DEPARTMENT_LABEL')}
                data-testid="department-dropdown"
                options={departmentOptions}
                inputRef={register}
                selected={departmentOptions.find(
                  department => department.value === value,
                )}
                onSelected={option => {
                  if (option) {
                    onChange(option.value);
                  }
                }}
                isDark
                full
              />
            )}
          />
        </div>
        <div className="mt-40 w-full text-center">
          <PrimaryButton
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            disabled={!department}
            onClick={() => nextStep(FormSteps.FORM_STEP_DEPARTMENT)}
            isDark
          />
        </div>
      </div>
      {/* Phone */}
      <div
        className={`${currentStep === 7 ? 'block' : 'hidden'} m-auto`}
        css={css`
          max-width: 363px;
        `}
      >
        <div>
          <span className="font-poppins font-semibold text-16 text-white">
            {t('FORM_BY_STEPS_PHONE_LABEL')}
          </span>
          <LazyPhoneInput
            isDark
            className="mt-8"
            defaultCountry={countryCode as string}
            onChange={e => {
              setUserInteraction(e.phoneNumber);
              setPhone(e);
            }}
          />
          {error && (
            <Banner
              message={t('FORM_BY_STEPS_BANNER_ERROR')}
              onDismiss={() => setError(undefined)}
              type="alert"
            />
          )}
        </div>
        <div className="mt-40 w-full text-center">
          <PrimaryButton
            ref={ref}
            className="w-full sm:w-280 sm:m-auto font-poppins font-semibold text-16 rounded-10"
            size="l"
            full
            type="submit"
            label={t('FORM_BY_STEPS_NEXT_BUTTON')}
            isDark
            disabled={!phone.isValid && !isSubmitting}
          />
        </div>
      </div>
    </form>
  );
};

export default FormBySteps;
